/**
 * Thin fetch wrapper used by every TanStack Query hook in the SPA.
 *
 * Rules baked into this wrapper:
 * - `credentials: 'include'` — Easy Auth session cookies set by Azure platform
 *   layer flow through to `/api/*` requests on the dev-server proxy.
 * - 15s default `AbortSignal.timeout` — RESEARCH.md walking-skeleton anti-pattern
 *   #9 ("loading spinners that spin forever on failure"). Every request HAS to
 *   fail loudly within a bounded time.
 * - 401 → in-place redirect to `/.auth/login/aad`. This is the auth-failure UX
 *   for an internal app where the user just needs to sign in again with Entra.
 * - 502 → throws `SalesforceUnavailableError`, which the SPA renders as the
 *   UX-07 "Couldn't reach Salesforce" banner (rather than a blank tile).
 * - Other 5xx → `ApiServerError`. Other 4xx → `ApiClientError`.
 *
 * Carried forward verbatim from packages/spa/src/lib/api-client.ts.
 * The 401 redirect target differs: Easy Auth sign-in vs SF OAuth.
 */
import type { ApiError } from '@saledash/shared';

const DEFAULT_TIMEOUT_MS = 15_000;

export class ApiClientError extends Error {
  override name = 'ApiClientError';
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export class ApiServerError extends Error {
  override name = 'ApiServerError';
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export class SalesforceUnavailableError extends Error {
  override name = 'SalesforceUnavailableError';
  constructor(
    message = "Couldn't reach Salesforce",
    public readonly status = 502,
  ) {
    super(message);
  }
}

/**
 * Thrown when the API responds 503 with `code: 'CACHE_NOT_READY'` or
 * `code: 'SNAPSHOT_NOT_READY'` — the sync-worker has not yet completed its
 * first aggregate poll / daily snapshot. The SPA renders a polite "Cache
 * warming up" banner (UX-07 + Plan 04) rather than the SF-unreachable
 * banner, because the SF connection is healthy; the system is just cold.
 */
export class CacheNotReadyError extends Error {
  override name = 'CacheNotReadyError';
  constructor(
    message = 'Cache warming up — first data refresh expected within ~7 minutes',
    public readonly status = 503,
    public readonly code:
      | 'CACHE_NOT_READY'
      | 'SNAPSHOT_NOT_READY' = 'CACHE_NOT_READY',
  ) {
    super(message);
  }
}

export class UnauthenticatedError extends Error {
  override name = 'UnauthenticatedError';
  constructor(public readonly status = 401) {
    super('Authentication required');
  }
}

interface ApiFetchOptions extends Omit<RequestInit, 'signal'> {
  signal?: AbortSignal;
  /** Custom timeout in ms; falls back to DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number;
  /**
   * Skip the automatic 401-redirect-to-login behavior. Useful for "is the
   * user signed in?" probes (useCroMe) where a 401 just means "no CRO access
   * yet" and the caller wants to handle the UI state itself — not pull the
   * user into an Entra sign-in flow they may not have access to.
   *
   * 05.3 D-16 reuses this same flag on the forecast query: the 401 branch
   * navigates immediately, so without opting out nothing is ever rendered and
   * the user never learns that signing in through Adeptia SSO is the way in.
   * Opting out lets the caller catch UnauthenticatedError and say so.
   */
  noLoginRedirect?: boolean;
}

/**
 * Resolve the sign-in target for an API path.
 *
 * EXPORTED because 05.3 D-16's access panel has to offer the SAME target the
 * automatic-redirect branch inside `apiFetch` would have used. A second copy of
 * this branch living in a component is a copy that drifts, and the failure mode
 * is silent: the panel sends a signed-out exec to Entra, Entra succeeds, and
 * they still have no Salesforce session — the exact loop D-16 exists to end.
 *
 *   /api/main/*, /api/2027-plan, /api/forecast  -> Salesforce OAuth web-server flow
 *   everything else                             -> Easy Auth (Entra group gate)
 *
 * SF OAuth cookie scoping: on a production host the target must be the api's
 * ABSOLUTE URL. The oauth2-redirect-state cookie set on /auth/login has to be
 * scoped to api.salesdashboard.adeptia.org or the browser will not send it on
 * the Salesforce callback, and SF answers "Invalid state".
 */
export function resolveLoginUrl(path: string): string {
  const needsSfLogin =
    path.startsWith('/api/main/') ||
    path.startsWith('/api/2027-plan') ||
    path.startsWith('/api/forecast');
  if (!needsSfLogin) return '/.auth/login/aad';

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.endsWith('salesdashboard.adeptia.org')) {
    return `${window.location.protocol}//api.salesdashboard.adeptia.org/auth/login`;
  }
  return '/auth/login';
}

/**
 * Typed fetch wrapper. Throws on non-2xx; returns parsed JSON on success.
 *
 * Callers should provide the TanStack Query `signal` as `opts.signal` so query
 * cancellation propagates into the underlying fetch.
 */
export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const {
    signal: callerSignal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers,
    noLoginRedirect = false,
    ...rest
  } = opts;

  // Compose the caller's AbortSignal with our own timeout signal — whichever
  // fires first cancels the request.
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const signal: AbortSignal = callerSignal
    ? AbortSignal.any([callerSignal, timeoutSignal])
    : timeoutSignal;

  const response = await fetch(path, {
    ...rest,
    credentials: 'include',
    signal,
    headers: {
      Accept: 'application/json',
      // eslint-disable-next-line @typescript-eslint/no-misused-spread -- callers pass plain-object headers; a Headers/tuple-array HeadersInit would be dropped (behavior follow-up in SUMMARY)
      ...headers,
    },
  });

  if (response.ok) {
    // 204 No Content path (e.g., POST /auth/logout)
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  // Parse error envelope when present (Plan 03/04 emit { error: { code, message } }).
  let parsedError: ApiError['error'] | undefined;
  try {
    const body = (await response.json()) as ApiError;
    parsedError = body.error;
  } catch {
    // Non-JSON error body — leave parsedError undefined.
  }

  const message =
    parsedError?.message ?? `Request to ${path} failed with status ${String(response.status)}`;
  const code = parsedError?.code;

  if (response.status === 401) {
    // 401 routing depends on which auth layer rejected the request:
    //   /api/main/*  → SF OAuth session gate (Plan 05-11) → /auth/login
    //   /api/cro/*   → Easy Auth (Entra group)            → /.auth/login/aad
    //   default      → Easy Auth (legacy CRO assumption)  → /.auth/login/aad
    // Both layers use error.code = 'UNAUTHENTICATED' so we discriminate by
    // request path. Without this branch, a missing SF session on /api/main/*
    // bounces the user to Easy Auth's login URL which doesn't exist in dev
    // and is the wrong identity layer in prod.
    // Choose the right login layer for a 401:
    //   /api/main/*  → SF OAuth (Web Server flow against api.* host)
    //   /api/cro/*   → Easy Auth (Entra group gate)
    //                  Both cro.* and apex now have Easy Auth configured
    //                  (apex enabled 2026-06-17 with AllowAnonymous + CRO
    //                  group restriction so general users still access /main).
    //
    // SF OAuth cookie scoping: the oauth2-redirect-state cookie set on
    // /auth/login MUST be scoped to api.salesdashboard.adeptia.org so the
    // browser sends it on the SF callback. A relative '/auth/login' from
    // either SPA host gets the cookie scoped to the SPA's origin, not the
    // api's — SF callback misses the cookie → "Invalid state". Redirect
    // to the api's absolute URL when running on a production host.
    // /api/2027-plan is SF-OAuth gated for the SAME broad exec audience as
    // /api/main (D-19 read gate) — a 401 there must bounce to the Salesforce
    // login, NOT Entra. Without this, exec users not in the CRO Entra group
    // (e.g. Geoff, Charles) get sent to /.auth/login/aad and can't open the tab.
    // /api/forecast is gated server-side by the SAME Salesforce OAuth sealed
    // session as /api/main (05.1 D-19 exec read gate), but it used to fall
    // through to the Entra target below. A signed-out exec was sent to the
    // wrong identity layer, and even after Entra succeeded they still had no
    // Salesforce session, so the 401 repeated. 05.3 D-16.
    const loginUrl = resolveLoginUrl(path);
    if (typeof window !== 'undefined' && !noLoginRedirect) {
      const isAbsolute = loginUrl.startsWith('http');
      const current = isAbsolute ? window.location.href : window.location.pathname;
      if (current !== loginUrl) {
        window.location.assign(loginUrl);
      }
    }
    throw new UnauthenticatedError(response.status);
  }

  if (response.status === 502 || code === 'SF_UNREACHABLE') {
    throw new SalesforceUnavailableError(message, response.status);
  }

  if (
    response.status === 503 &&
    (code === 'CACHE_NOT_READY' || code === 'SNAPSHOT_NOT_READY')
  ) {
    throw new CacheNotReadyError(message, response.status, code);
  }

  if (response.status >= 500) {
    throw new ApiServerError(message, response.status, code);
  }

  throw new ApiClientError(message, response.status, code, parsedError?.details);
}
